import React, { useMemo } from 'react';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { useShellNav } from '../shell/ShellNavContext';
import { shellSx, shellTokens } from '../shell/theme/shellTheme';
import { useFleet } from '../strategyDesk/hooks/useFleet';
import TodayAlerts from './components/TodayAlerts';
import TodayKpiStrip from './components/TodayKpiStrip';
import TodayOpenPositions from './components/TodayOpenPositions';
import { countActionableAlerts, deriveTodayAlerts } from './todayAlerts';

const OPEN_POSITIONS_TOP_N = 5;

const QUICK_ACTIONS = [
    { label: 'Place order', destination: 'trade', subTab: 'orders' },
    { label: 'Fleet view', destination: 'strategies', subTab: 'fleet' },
    { label: 'Check pipeline', destination: 'platform', subTab: 'pipeline' },
    { label: 'Create interrupt', destination: 'admin', subTab: 'control' },
];

const subTabSx = {
    border: `1px solid ${shellTokens.border}`,
    bgcolor: shellTokens.surface,
    borderRadius: '8px',
    px: 1.75,
    py: 0.85,
    fontSize: '0.78rem',
    fontWeight: 700,
    color: shellTokens.muted,
    textTransform: 'none',
    minWidth: 0,
};

const Panel = ({ eyebrow, title, children }) => (
    <Box sx={{ ...shellSx.panel, mb: 1.75 }}>
        <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${shellTokens.border}` }}>
            <Typography
                sx={{
                    fontSize: '0.62rem',
                    fontWeight: 700,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    color: shellTokens.muted,
                }}
            >
                {eyebrow}
            </Typography>
            <Typography sx={{ fontWeight: 700, fontSize: '0.92rem', mt: 0.25 }}>{title}</Typography>
        </Box>
        <Box sx={{ p: '14px 16px' }}>{children}</Box>
    </Box>
);

const TodayPage = () => {
    const { setDestination } = useShellNav();
    const { data, error, isLoading, isFetching, refetch } = useFleet();

    const instances = useMemo(() => data?.instances || [], [data?.instances]);
    const positions = useMemo(() => data?.positions || [], [data?.positions]);
    const summary = data?.summary;

    const alerts = useMemo(
        () => deriveTodayAlerts(instances, positions),
        [instances, positions]
    );
    const alertCount = useMemo(() => countActionableAlerts(alerts), [alerts]);

    const goToStrategies = () => setDestination('strategies', 'fleet');

    return (
        <Box>
            {error ? (
                <Alert
                    severity="error"
                    action={
                        <Button color="inherit" size="small" onClick={() => refetch()}>
                            Retry
                        </Button>
                    }
                    sx={{ mb: 1.75 }}
                >
                    Fleet data unavailable — {error.message}
                </Alert>
            ) : null}

            <TodayKpiStrip summary={summary} alertCount={alertCount} loading={isLoading} />

            {isFetching && !isLoading ? (
                <Typography sx={{ fontSize: '0.72rem', color: shellTokens.muted, mb: 1.25 }}>
                    Refreshing fleet data…
                </Typography>
            ) : null}

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                    gap: 1.75,
                    mb: 0,
                }}
            >
                <Panel eyebrow="Alerts" title="Needs attention">
                    <TodayAlerts alerts={alerts} loading={isLoading} />
                </Panel>

                <Panel eyebrow="Open book" title={`Open positions (top ${OPEN_POSITIONS_TOP_N})`}>
                    <TodayOpenPositions
                        positions={positions}
                        instances={instances}
                        loading={isLoading}
                        onNavigate={goToStrategies}
                    />
                </Panel>
            </Box>

            <Panel eyebrow="Quick actions" title="Common operator tasks">
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    {QUICK_ACTIONS.map((action) => (
                        <Button
                            key={action.label}
                            sx={subTabSx}
                            onClick={() => setDestination(action.destination, action.subTab)}
                        >
                            {action.label}
                        </Button>
                    ))}
                </Stack>
            </Panel>
        </Box>
    );
};

export default TodayPage;
